import { readJwtToken } from "../../../util/decode-jwt";
import { Observable } from "../../../util/observable";
import { UserRoles } from "../driven/enums";
import { ICryptoProvider } from "../driven/providers/cryptoFactory";
import { IlocalStorageProvider } from "../driven/providers/localStorageProvider";

export type IAuthController = ReturnType<typeof AuthControllerFactory>;

/**
 * Cognito groups that are allowed into this app. A user is considered to have
 * access if they belong to AT LEAST ONE of these. Setup-only technicians
 * (`setup_app_user`) must pass through here so the AppRouter can land them on
 * the setup shell — historically only `home_health_patient` was kept, which
 * stripped the technician's access and dumped them on the "Not allowed" page.
 */
const PATIENT_APP_GROUP = "home_health_patient";
const SETUP_APP_GROUP = "setup_app_user";
const filterAppGroups = (groups: string[] | undefined | null): string[] => {
  if (!groups || groups.length === 0) return [];
  return groups.includes(PATIENT_APP_GROUP) || groups.includes(SETUP_APP_GROUP)
    ? groups
    : [];
};

/**
 * Cognito user pools may store names on `custom:first_name` / `custom:last_name`
 * (web + staff flows), or on standard OIDC claims (`given_name`, `family_name`,
 * `name`) — e.g. native self-signup. Missing attributes must not become the
 * literal string "undefined" in `fullName` (JS template coercion).
 */
const resolveCognitoUserDisplayNames = (
  attrs: Record<string, unknown>
): { firstName: string; lastName: string; fullName: string } => {
  const pick = (key: string): string => {
    const v = attrs[key];
    if (v == null) return "";
    const s = String(Array.isArray(v) ? v[0] : v).trim();
    return s;
  };

  let firstName = pick("custom:first_name") || pick("given_name");
  let lastName = pick("custom:last_name") || pick("family_name");
  const nameOnly = pick("name");
  if (!firstName && !lastName && nameOnly) {
    const parts = nameOnly.split(/\s+/).filter(Boolean);
    firstName = parts[0] ?? "";
    lastName = parts.length > 1 ? parts.slice(1).join(" ") : "";
  }

  let fullName = "";
  if (firstName && lastName) {
    fullName = `${lastName}, ${firstName}`;
  } else if (firstName || lastName) {
    fullName = [lastName, firstName].filter(Boolean).join(", ");
  } else if (nameOnly) {
    fullName = nameOnly;
  }

  return { firstName, lastName, fullName };
};

export const AuthControllerFactory = ({
  AuthService,
  localStorageProvider,
  cryptoJsProvider,
  AuthObserver,
  circadiaEngineProvider,
  electronEngineProvider,
}: {
  AuthService: IAuthService;
  localStorageProvider: IlocalStorageProvider;
  cryptoJsProvider: (secret: string) => ICryptoProvider;
  AuthObserver: InstanceType<typeof Observable>;
  circadiaEngineProvider: ICircadiaEngineProvider;
  electronEngineProvider: IElectronEngineProvider;
}) => {
  // Do NOT cache as boolean at factory init — on Android the bridge is
  // injected asynchronously and the value would be permanently false.
  const isCircadiaEngine = () => circadiaEngineProvider.isWindowCircadia();
  const isElectronEngine = electronEngineProvider.isWindowElectron();

  // Resolve the signed-in user from the Amplify web session. Used both as the
  // normal web path and as a fallback when the native engine is present but has
  // no stored session — e.g. users who authenticated via the Amplify web
  // fallback while the native bridge was unavailable. Their tokens live in the
  // browser under the WEB Cognito app client (different from the native client),
  // so we keep using the Amplify session rather than importing it into native.
  const getWebUser = async () => {
    try {
      const currentSession = await AuthService.getCurrentSessionPayload();
      const currentUserInfo = await AuthService.getCurrentUserInfo();

      const attributes = currentUserInfo?.attributes!!;
      const { sub: userID, email } = attributes;
      const { firstName, lastName, fullName } = resolveCognitoUserDisplayNames(
        attributes as unknown as Record<string, unknown>
      );
      const groups = currentSession["cognito:groups"] as string[];
      const homeHealthId = (attributes as any)["custom:home_health_id"] as string;
      let role = "";
      if (groups) {
        groups.forEach((group) => {
          if (group === "MedicalGroup-BillingPractitioner") {
            role = "Billing Practitioner";
          }
          if (group === "MedicalGroup-Administrator") {
            role = "Administrator";
          }
          if (group === "AdmissionTeam") {
            role = "Admission Team";
          }
          if (group === "VRN") {
            role = "VRN";
          }
          if (group === "AccountManager") {
            role = "Account Manager";
          }
          if (group === "home_health_patient") {
            role = "Home Health Patient";
          }
        });
      }
      return {
        success: true as const,
        data: {
          user: {
            groups: filterAppGroups(groups),
            userID,
            fullName,
            firstName,
            lastName,
            status: true,
            email,
            role,
            homeHealthId,
          },
        },
      };
    } catch (error) {
      return {
        success: false as const,
        error: error as ICognitoErrorType,
      };
    }
  };

  // Attempt the web → native session migration at most once per app run so a
  // failed/empty import can't loop with getCurrentUser's recursive retry.
  let webSessionMigrationAttempted = false;

  /**
   * Import the web (Amplify) session from browser localStorage into the native
   * secure storage, so a user who signed in via the web fallback (while the
   * native bridge was unavailable) becomes a first-class native session that
   * can drive token-dependent native features.
   *
   * The refresh token that Amplify persists in localStorage is bound to the WEB
   * Cognito app client (VITE_COGNITO_APP_CLIENT_ID), which differs from the
   * native client. We therefore hand the native side that client id (plus the
   * remembered-device key when device tracking is on) so it refreshes against
   * the correct client. Resolves true only when the native import succeeds.
   */
  const tryImportWebSessionToNative = async (): Promise<boolean> => {
    try {
      const clientId = import.meta.env.VITE_COGNITO_APP_CLIENT_ID as
        | string
        | undefined;
      if (!clientId) return false;

      const base = `CognitoIdentityServiceProvider.${clientId}`;
      const username = window.localStorage.getItem(`${base}.LastAuthUser`);
      if (!username) return false;

      const prefix = `${base}.${username}`;
      const refreshToken = window.localStorage.getItem(
        `${prefix}.refreshToken`
      );
      if (!refreshToken) return false;

      const deviceKey =
        window.localStorage.getItem(`${prefix}.deviceKey`) ?? undefined;
      const deviceGroupKey =
        window.localStorage.getItem(`${prefix}.deviceGroupKey`) ?? undefined;

      await circadiaEngineProvider.cognitoImportWebSession({
        clientId,
        username,
        refreshToken,
        deviceKey,
        deviceGroupKey,
      });
      return true;
    } catch {
      return false;
    }
  };

  const getCurrentUser: () => IControllerPromiseResponse<IGetCurrentUserResponse> =
    async () => {
      if (localStorageProvider.get("sso") === "true") {
        try {
          const currentUserInfo = await AuthService.getCurrentUserInfo();
          const attributes = currentUserInfo?.attributes!!;
          const { sub: userID, email } = attributes;
          const { firstName, lastName, fullName } =
            resolveCognitoUserDisplayNames(
              attributes as unknown as Record<string, unknown>
            );
          return {
            success: true,
            data: {
              user: {
                userID,
                fullName,
                firstName,
                lastName,
                status: true,
                email,
                groups: ["cip_users"],
                role: "",
                pccOrgUuid: attributes["custom:pcc_org_uuid"],
                pccEmail: attributes["custom:pcc_email"],
                phoneNumber: attributes["phone_number"],
                title: attributes["custom:m_credentials"],
              },
            },
          };
        } catch (error) {
          return {
            success: false,
            error: error as ICognitoErrorType,
          };
        }
      }
      if (localStorageProvider.get("zpax") === "true") {
        try {
          const currentUserInfo = await AuthService.getCurrentUserInfo();
          const attributes = currentUserInfo?.attributes!!;
          const { sub: userID, email } = attributes;
          const { firstName, lastName, fullName } =
            resolveCognitoUserDisplayNames(
              attributes as unknown as Record<string, unknown>
            );
          const groups = (attributes as any)["cognito:groups"] as string[];

          return {
            success: true,
            data: {
              user: {
                userID,
                fullName,
                firstName,
                lastName,
                status: true,
                email,
                groups: groups || ["cip_users"],
                role: "",
              },
            },
          };
        } catch (error) {
          return {
            success: false,
            error: error as ICognitoErrorType,
          };
        }
      }
      if (isElectronEngine) {
        try {
          const currentSession = await AuthService.getCurrentSessionPayload();
          const currentUserInfo = await AuthService.getCurrentUserInfo();

          const attributes = currentUserInfo?.attributes!!;
          const { sub: userID, email } = attributes;
          const { firstName, lastName, fullName } =
            resolveCognitoUserDisplayNames(
              attributes as unknown as Record<string, unknown>
            );
          const idToken = currentSession;
          const groups = idToken["cognito:groups"] as string[];
          const homeHealthId = (attributes as any)[
            "custom:home_health_id"
          ] as string;
          let role = "";
          if (groups) {
            groups.forEach((group) => {
              if (group === "MedicalGroup-BillingPractitioner") {
                role = "Billing Practitioner";
              }
              if (group === "MedicalGroup-Administrator") {
                role = "Administrator";
              }
              if (group === "AdmissionTeam") {
                role = "Admission Team";
              }
              if (group === "VRN") {
                role = "VRN";
              }
              if (group === "AccountManager") {
                role = "Account Manager";
              }
              if (group === "home_health_patient") {
                role = "home_health_patient";
              }
            });
          }
          return {
            success: true,
            data: {
              user: {
                groups,
                userID,
                fullName,
                firstName,
                lastName,
                status: true,
                email,
                role,
                homeHealthId,
              },
            },
          };
        } catch (error) {
          return {
            success: false,
            error: error as ICognitoErrorType,
          };
        }
      }
      if (isCircadiaEngine()) {
        try {
          const jwtToken:
            | {
                IdToken: string;
                AccessToken: string;
              }
            | undefined = await new Promise((resolve, reject) => {
            const observerEvent = ({
              type,
              payload,
            }: {
              type: string;
              payload: {
                success: boolean;
                error?: string;
              };
            }) => {
              AuthObserver.unsubscribe(observerEvent);
              if (type === "login") {
                if (!payload.success) {
                  reject(payload.error);
                }
              } else if (type === "pinRequired") {
                reject("pinRequired");
              }
            };
            AuthObserver.subscribe(observerEvent);
            (async () => {
              await circadiaEngineProvider
                .cognitoTokens()
                .then((tokens: { IdToken: string; AccessToken: string }) => {
                  resolve(tokens);
                })
                .catch((err: any) => {
                  AuthObserver.unsubscribe(observerEvent);
                  reject(err?.message || err || "Token retrieval failed");
                });
            })();
          });
          const currentUserInfo = readJwtToken(
            jwtToken ? jwtToken.IdToken : ""
          ) as Record<string, unknown> | null;

          if (currentUserInfo && currentUserInfo.sub && currentUserInfo.email) {
            const { sub: userID, email } = currentUserInfo as {
              sub: string;
              email: string;
            };
            const { firstName, lastName, fullName } =
              resolveCognitoUserDisplayNames(currentUserInfo);
            const groups = currentUserInfo["cognito:groups"] as string[];
            const homeHealthId = currentUserInfo[
              "custom:home_health_id"
            ] as string;
            let role = "";
            if (groups) {
              groups.forEach((group) => {
                if (group === "MedicalGroup-BillingPractitioner") {
                  role = "Billing Practitioner";
                }
                if (group === "MedicalGroup-Administrator") {
                  role = "Administrator";
                }
                if (group === "AdmissionTeam") {
                  role = "Admission Team";
                }
                if (group === "VRN") {
                  role = "VRN";
                }
                if (group === "AccountManager") {
                  role = "Account Manager";
                }
                if (group === "home_health_patient") {
                  role = "Home Health Patient";
                }
              });
            }

            return {
              success: true,
              data: {
                user: {
                  groups: filterAppGroups(groups),
                  userID,
                  fullName,
                  firstName,
                  lastName,
                  status: true,
                  email,
                  role,
                  homeHealthId,
                },
              },
            };
          }
          throw new Error("No current user info");
        } catch (error) {
          // Native engine is present but has no stored session. First, try to
          // migrate the web (Amplify) session from localStorage into native so
          // the user becomes a full native session (token refreshes, native
          // features). Attempted at most once per app run; on success we resolve
          // the user through the native path via a single recursive retry.
          if (!webSessionMigrationAttempted) {
            webSessionMigrationAttempted = true;
            const imported = await tryImportWebSessionToNative();
            if (imported) {
              return await getCurrentUser();
            }
          }
          // Migration unavailable/failed — fall back to using the Amplify web
          // session directly so users who signed in via the web fallback are
          // not logged out. If the web session is also empty, surface the
          // original failure and the user is routed to login as expected.
          const webUser = await getWebUser();
          if (webUser.success) return webUser;
          return {
            success: false,
            error: error as ICognitoErrorType,
          };
        }
      } else {
        return getWebUser();
      }
    };

  const createPin = async ({
    user,
    key,
    value,
  }: {
    user: User;
    key: string;
    value: string;
  }) => {
    try {
      if (isElectronEngine) {
        const response = await AuthService.createSSPin({ pin: value });

        if (typeof response === "object" && response.status === "error") {
          return { success: false, error: response.message };
        }

        localStorageProvider.set("userHasPinSetup", "true");
        localStorageProvider.set(
          "internalUse",
          JSON.stringify({ email: user.email, keyToCheck: key })
        );
        return {
          success: true,
        };
      } else {
        const { email } = user;

        await AuthService.createSSPin({ pin: value });
        await AuthService.saveSSValue({
          email,
          key,
          value,
        });

        localStorageProvider.set("userHasPinSetup", "true");
        localStorageProvider.set(
          "internalUse",
          JSON.stringify({ email: user.email, keyToCheck: key })
        );
        return {
          success: true,
        };
      }
    } catch (error) {
      return { success: false, error: error };
    }
  };

  const verifyPin = async ({
    email,
    key,
    value,
  }: {
    email: string;
    key: string;
    value: string;
  }) => {
    try {
      if (isElectronEngine) {
        const response = await electronEngineProvider.verifyPin(value);
        if (response.status === "error") {
          return { success: false, error: response.message };
        }
        return {
          success: true,
        };
      }

      if (isCircadiaEngine()) {
        await AuthService.cognitoProvidePin(value);
      }
      const response = await AuthService.loadSSValue({
        email,
        key,
        value,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: (error as { message: string })?.message,
      };
    }
  };

  const verifyBio = async ({ email, key }: { email: string; key: string }) => {
    try {
      const response = await AuthService.loadSSValueBio({
        email,
        key,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const saveUserPass = async ({
    jwtToken,
    email,
    key,
    value,
  }: {
    jwtToken: string;
    email: string;
    key: string;
    value: string;
  }) => {
    try {
      await AuthService.createSSPin({ pin: value });
      const response = await AuthService.saveSSValue({
        email,
        key,
        value,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return { success: false, error: error };
    }
  };

  const getUserPass = async ({
    email,
    key,
    value,
  }: {
    email: string;
    key: string;
    value: string;
  }) => {
    try {
      if (isCircadiaEngine()) {
        await AuthService.cognitoProvidePin(value);
      }
      const response = await AuthService.loadSSValue({
        email,
        key,
        value,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error,
      };
    }
  };

  const changePin = async ({
    user,
    key,
    value,
  }: {
    user: User;
    key: string;
    value: string;
  }) => {
    try {
      if (isElectronEngine) {
        const tokens = await electronEngineProvider.retrieveTokens();
        const response = await electronEngineProvider.changePin({
          pin: value,
          token: tokens.idToken,
        });

        if (response.status === "error") {
          return { success: false, error: response.message };
        }
        return {
          success: true,
        };
      }

      const { email } = user;
      await AuthService.changeSSPin({ pin: value });
      const response = await AuthService.saveSSValue({
        email,
        key,
        value,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: "Error Changing Pin" },
      };
    }
  };

  const login: ILogin = async (payload) => {
    try {
      const user = await AuthService.login(payload);
      const returnData = {
        user: user!!,
        challenge: "",
      };
      if (user?.challengeName) {
        returnData.challenge = user.challengeName;
      }

      return {
        success: true,
        data: returnData,
      };
    } catch (error) {
      return {
        success: false,
        error: isCircadiaEngine()
          ? { message: error as string }
          : (error as ICognitoErrorType),
      };
    }
  };

  const ssoLogin: ILogin = async ({
    token,
    expires_at,
  }: {
    token: string;
    expires_at: number;
  }) => {
    try {
      const user = await AuthService.ssoLogin({
        token,
        expires_at,
      });
      const returnData = {
        user: user!!,
        challenge: "",
      };

      console.log("return ", returnData);

      return {
        success: true,
        data: returnData,
      };
    } catch (error) {
      return {
        success: false,
        error: isCircadiaEngine()
          ? { message: error as string }
          : (error as ICognitoErrorType),
      };
    }
  };

  const checkPassword: ICheckPasswordController = async (payload) => {
    if (electronEngineProvider.isWindowElectron()) {
      const response = await AuthService.checkPassword({
        oldPassword: payload.password,
      });

      if (response.status === "success") {
        return {
          success: true,
          data: {
            user: response.data!!,
            challenge: "",
          },
        };
      } else {
        return {
          success: false,
          error: { message: response.message },
        };
      }
    } else {
      return login(payload);
    }
  };

  const forgotPassword: IForgotPassword = async (payload) => {
    try {
      const response = await AuthService.forgotPassword(payload);
      if (response?.status === "error") {
        return {
          success: false,
          error: { message: response.message },
        };
      }
      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: error as ICognitoErrorType,
      };
    }
  };

  const resetPassword: IResetPassword = async (payload) => {
    try {
      const res = await AuthService.resetPassword(payload);
      if (res && typeof res !== "string") {
        if (res.status === "error") {
          return { success: false, error: { message: res.message } };
        } else {
          return {
            success: true,
          };
        }
      }
      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: error as ICognitoErrorType,
      };
    }
  };

  const completeSMSMFAChallenge: ICompleteSMSMFAChallenge = async (payload) => {
    try {
      const data = await AuthService.completeSMSMFAChallenge(payload);
      return {
        success: true,
        data: {
          user: data!!,
          challenge: "",
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error as ICognitoErrorType,
      };
    }
  };

  const completeNewPasswordChallenge: ICompleteNewPasswordChallenge = async (
    payload
  ) => {
    try {
      const data = await AuthService.completeNewPasswordChallenge(payload);
      return {
        success: true,
        data: data!!,
      };
    } catch (error) {
      return { success: false, error: error as ICognitoErrorType };
    }
  };

  const logoutUser = async () => {
    try {
      await AuthService.logout();
      return { success: true };
    } catch (error) {
      return { success: false };
    }
  };

  const changePassword = async ({
    oldPassword,
    newPassword,
  }: IChangePassword) => {
    try {
      if (isElectronEngine) {
        const data = await AuthService.changePassword({
          oldPassword,
          newPassword,
        });
        return {
          success: true,
          data,
        };
      } else {
        let user: User | string;
        if (isCircadiaEngine()) {
          user = (await AuthService.getAuthenticationAccessToken()) as string;
        } else {
          user = (await AuthService.currentAuthenticatedUser()) as User;
        }
        if (!user) {
          throw new Error("No current Authenticated User");
        }
        const data = await AuthService.changePassword({
          user,
          oldPassword,
          newPassword,
        });
        return {
          success: true,
          data,
        };
      }
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
        data: null,
      };
    }
  };

  const saveEncryptedUserPass = ({ userPass, secretKey }: IEncrypted) => {
    const { encrypted } = cryptoJsProvider(secretKey);
    if (userPass) {
      const encryptedUserPass = cryptoJsProvider(secretKey).encrypt(userPass);
      localStorageProvider.set(encrypted.userPass, encryptedUserPass);
    }
  };

  const getDecryptedUserPass = (secretKey: string): string | null => {
    const { encrypted } = cryptoJsProvider(secretKey);
    const encryptedUserPass = localStorageProvider.get(encrypted.userPass);
    if (!encryptedUserPass) {
      return null;
    }
    const decryptedUserPass =
      cryptoJsProvider(secretKey).decrypt(encryptedUserPass);
    return decryptedUserPass;
  };

  const saveEncryptedCredentials = ({ credential, secretKey }: IEncrypted) => {
    const { encrypted } = cryptoJsProvider(secretKey);
    const encryptedCredentials =
      cryptoJsProvider(secretKey).encrypt(credential);
    localStorageProvider.set(encrypted.credentials, encryptedCredentials);
  };

  const getDecryptedCredentials = (secretKey: string): string | null => {
    const { encrypted } = cryptoJsProvider(secretKey);
    const encryptedCredentials = localStorageProvider.get(
      encrypted.credentials
    );
    if (!encryptedCredentials) {
      return null;
    }
    const decryptedCredentials =
      cryptoJsProvider(secretKey).decrypt(encryptedCredentials);
    return decryptedCredentials;
  };

  return {
    changePassword,
    completeNewPasswordChallenge,
    completeSMSMFAChallenge,
    getAuthenticationToken: AuthService.getAuthenticationToken,
    getCurrentSessionPayload: AuthService.getCurrentSessionPayload,
    getCurrentUserInfo: AuthService.getCurrentUserInfo,
    getCurrentUser,
    login,
    ssoLogin,
    logoutUser,
    isBP: (role: string) => role === UserRoles.BP,
    isBA: (role: string) => role === UserRoles.BA,
    isAT: (role: string) => role === UserRoles.AT,
    isVRN: (role: string) => role === UserRoles.VRN,
    isAM: (role: string) => role === UserRoles.AM,
    getDecryptedCredentials,
    saveEncryptedCredentials,
    saveEncryptedUserPass,
    getDecryptedUserPass,
    createPin,
    verifyPin,
    verifyBio,
    changePin,
    saveUserPass,
    getUserPass,
    forgotPassword,
    resetPassword,
    checkPassword,
  };
};
